import {
  Autocomplete,
  Box,
  Checkbox,
  Grid,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import React, { useEffect, useMemo, useState } from 'react';
import { BackButton, Button, ErrorMessage, Heading, Input, Select, UploadImage, Loader } from '../../components/Common';
import { useLocation, useNavigate } from 'react-router-dom';
import { bloodGroupList, countryList } from '../../helper';
import { useMessage } from '../../components/Common/Message/MessageContext';
import { useGetCityListQuery, useGetStateListQuery } from '../../services/Location';
import {
  useAddNewSubAdminMutation,
  useUpdateSubAdminMutation,
  useGetAdminByCityQuery,
  useGetSubAdminDetailQuery,
  useGetExtraPincodeByCityQuery,
  useGetPincodeByCityQuery,
} from '../../services/SubAdmin';
import { useGetAdminDetailQuery } from '../../services/Admin';
import { getAllData } from '../../helper/indexDB';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AddPincodeAdmin = () => {
  const { showMessage } = useMessage();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState([]);
  const [country, setCountry] = useState(1);
  const [states, setStates] = useState(null);
  const [city, setCity] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [aadharImage, setAadharImage] = useState([]);
  const [bloodGroup, setBloodGroup] = useState(null);
  const [selectedPincodes, setSelectedPincodes] = useState([]);

  const [formValues, setFormValues] = useState({
    name: '',
    mobile: '',
    email: '',
    guardianName: '',
    guardianMobile: '',
    address: '',
    panNo: '',
    dob: '',
  });

  const [errorMessages, setErrorMessages] = useState({});
  const [profileErrorMessage, setProfileErrorMessage] = useState('');
  const [addharErrorMessage, setAddharErrorMessage] = useState('');
  const [orderDialog, setOrderDialog] = useState({
    open: false,
    pincode: '',
    orders: [],
    totalOrders: 0,
  });

  const { data: cityLst, error: cityError, isLoading: cityLoading } = useGetCityListQuery(states, { skip: !states });
  const cityList = cityLst?.data;
  const { data: subAdminLst, error: subAdminError, isLoading: subAdminLoading } = useGetSubAdminDetailQuery(state?.id, { skip: !state, refetchOnMountOrArgChange: true });
  const subAdminDetails = subAdminLst?.data;

  const { data: stateLst, error: stateError, isLoading: stateLoading } = useGetStateListQuery();
  const stateList = stateLst?.data;

  const { data: adminLst, error: adminListError, isLoading: adminListLoading } = useGetAdminByCityQuery();
  const adminList = adminLst?.AdminData;

  const selectedAdmin = useMemo(() => {
    if (!admin || !adminList?.length) return null;
    return adminList.find((a) => String(a.id) === String(admin)) || null;
  }, [admin, adminList]);

  const isSelectedAdminSuper = useMemo(() => {
    return String(selectedAdmin?.type) === '1';
  }, [selectedAdmin]);

  const { data: adminDetailData, isLoading: adminDetailLoading } = useGetAdminDetailQuery(admin, {
    skip: !admin,
    refetchOnMountOrArgChange: true,
  });

  const adminPincodes = useMemo(() => {
    const pins = adminDetailData?.data?.pincodes || [];
    return pins
      .filter((p) => p)
      .map((pincode) => ({ pincode: String(pincode) }));
  }, [adminDetailData]);

  const { data: cityExtraPincodeRes, isLoading: cityExtraPincodeLoading } = useGetExtraPincodeByCityQuery(city, {
    skip: !city || !isSelectedAdminSuper,
    refetchOnMountOrArgChange: true,
  });

  const cityPincodesForSuperAdmin = useMemo(() => {
    const rows = cityExtraPincodeRes?.data || [];
    return rows.map((r) => ({ pincode: String(r.pincode) }));
  }, [cityExtraPincodeRes]);

  const targetCityForOwnership = city;
  const { data: pincodeOwnershipData, isLoading: pincodeOwnershipLoading } = useGetPincodeByCityQuery(
    targetCityForOwnership,
    {
      skip: !targetCityForOwnership,
      refetchOnMountOrArgChange: true,
    }
  );

  const pincodeOwnershipMap = useMemo(() => {
    const map = {};
    const list = pincodeOwnershipData?.data || [];
    list.forEach((item) => {
      const pin = String(item.pincode || '');
      if (pin) {
        map[pin] = item.pinCodeAdminId || null;
      }
    });
    return map;
  }, [pincodeOwnershipData]);

  const [adminRole, setAdminRole] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      const storedData = await getAllData();
      setAdminRole(storedData?.[0]?.value.type);

      if (storedData?.[0]?.value.type !== 1) {
        setCity(storedData?.[0]?.value.city);
        setStates(storedData?.[0]?.value.state);
        setAdmin(storedData?.[0]?.value.id);
      }
    };

    fetchData();
  }, []);

  const handleStateChange = (e) => setStates(e.target.value)
  const handleCountryChange = (e) => setCountry(e.target.value)
  const handleCityChange = (e) => setCity(e.target.value)
  const handleBloodGroupChange = (e) => setBloodGroup(e.target.value);

  const handleAdminChange = (e) => {
    const selectedId = parseInt(e.target.value);
    setAdmin(selectedId);

    const selected = adminList?.find(item => String(item.id) === String(selectedId));
    if (selected && String(selected.type) !== '1') {
      setCity(selected.city);
      setStates(selected.state);
    }

    setSelectedPincodes([]);
  }

  const handleCloseOrderDialog = () => {
    // Re-add the blocked pincode back to selected pincodes
    if (orderDialog.pincode && !selectedPincodes.includes(orderDialog.pincode)) {
      setSelectedPincodes(prev => [...prev, orderDialog.pincode]);
    }

    setOrderDialog({
      open: false,
      pincode: '',
      orders: [],
      totalOrders: 0,
    });
  };

  const [addAdmin, { isLoading: isAdding }] = useAddNewSubAdminMutation();
  const [updateAdmin, { isLoading: isUpdating }] = useUpdateSubAdminMutation();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if ((name === 'mobile' || name === 'guardianMobile') && value.length > 10) return;
    setFormValues({ ...formValues, [name]: value });
    if (errorMessages[name]) {
      setErrorMessages((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateField = (field, fieldName) => {
    if (!field) {
      setErrorMessages(prevState => ({ ...prevState, [fieldName]: `${fieldName} is a mandatory field.` }));
      return true;
    }
    setErrorMessages(prevState => ({ ...prevState, [fieldName]: '' }));
    return false;
  };

  const validateMobile = (field, fieldName) => {
    if (!field) {
      setErrorMessages(prevState => ({ ...prevState, [fieldName]: `${fieldName} is a mandatory field.` }));
      return true;
    } else if (field.toString().length !== 10) {
      setErrorMessages(prevState => ({ ...prevState, [fieldName]: `${fieldName} must be exactly 10 digits.` }));
      return true;
    }
    setErrorMessages(prevState => ({ ...prevState, [fieldName]: '' }));
    return false;
  };

  useEffect(() => {
    if (state?.id) return;

    if (!admin) {
      setSelectedPincodes([]);
      return;
    }

    if (isSelectedAdminSuper) {
      if (!city || !cityPincodesForSuperAdmin.length) {
        setSelectedPincodes([]);
        return;
      }
      const freePins = cityPincodesForSuperAdmin
        .filter((p) => !pincodeOwnershipMap[p.pincode])
        .map((p) => p.pincode);
      setSelectedPincodes(freePins);
      return;
    }

    if (!adminPincodes.length) {
      setSelectedPincodes([]);
      return;
    }
    const freeAdminPins = adminPincodes
      .filter((p) => !pincodeOwnershipMap[p.pincode])
      .map((p) => p.pincode);
    setSelectedPincodes(freeAdminPins);
  }, [admin, adminPincodes, state?.id, isSelectedAdminSuper, city, cityPincodesForSuperAdmin, pincodeOwnershipMap]);

  const handleFormSubmit = async () => {
    setErrorMessages({});
    setProfileErrorMessage('');
    setAddharErrorMessage('');

    let hasError = false;

    const hasProfileImage =
      profileImage instanceof File ||
      (Array.isArray(profileImage) && profileImage.length > 0 && profileImage[0] instanceof File) ||
      (typeof profileImage === 'string' && profileImage.length > 0);

    const hasAadharImage =
      aadharImage instanceof File ||
      (Array.isArray(aadharImage) && aadharImage.length > 0 && aadharImage[0] instanceof File) ||
      (typeof aadharImage === 'string' && aadharImage.length > 0);

    if (!state?.id) {
      if (!hasProfileImage) {
        setProfileErrorMessage('Please select a profile image to upload.');
        hasError = true;
      }

      if (!hasAadharImage) {
        setAddharErrorMessage('Please select an aadhar image to upload.');
        hasError = true;
      }
    }

    let pincodeError = false;
    if (admin && selectedPincodes.length === 0) {
      setErrorMessages(prev => ({ ...prev, pincode: 'Please select at least one pincode assigned to this admin.' }));
      pincodeError = true;
      hasError = true;
    } else {
      setErrorMessages(prev => ({ ...prev, pincode: '' }));
    }

    const nameError = validateField(formValues.name, 'name');
    const adminError = validateField(admin, 'admin');
    const mobileError = validateMobile(formValues.mobile, 'mobile');
    const emailError = validateField(formValues.email, 'email');
    const guardianNameError = validateField(formValues.guardianName, 'guardianName');
    const guardianMobileError = validateMobile(formValues.guardianMobile, 'guardianMobile');
    const countryError = validateField(country, 'country');
    const stateError = validateField(states, 'state');
    const cityError = validateField(city, 'city');
    const addressError = validateField(formValues.address, 'address');
    const panNoError = validateField(formValues.panNo, 'panNo');
    const dobError = validateField(formValues.dob, 'dob');
    const bloodGroupError = validateField(bloodGroup, 'bloodGroup');

    if (
      nameError || mobileError || emailError || guardianNameError || guardianMobileError ||
      countryError || stateError || cityError || addressError || hasError ||
      panNoError || dobError || bloodGroupError || adminError || pincodeError
    ) {
      showMessage("error", "Please fill all mandatory fields");
      return;
    }

    const formData = new FormData();

    if (profileImage instanceof File) {
      formData.append('profileImage', profileImage);
    } else if (Array.isArray(profileImage) && profileImage.length > 0 && profileImage[0] instanceof File) {
      formData.append('profileImage', profileImage[0]);
    }

    if (aadharImage instanceof File) {
      formData.append('aadharImage', aadharImage);
    } else if (Array.isArray(aadharImage) && aadharImage.length > 0 && aadharImage[0] instanceof File) {
      formData.append('aadharImage', aadharImage[0]);
    }

    formData.append('bloodGroup', bloodGroup);
    formData.append('name', formValues.name);
    formData.append('mobile', formValues.mobile);
    formData.append('email', formValues.email);
    formData.append('guardianName', formValues.guardianName);
    formData.append('guardianMobile', formValues.guardianMobile);
    formData.append('country', country);
    formData.append('state', states);
    formData.append('city', city);
    formData.append('address', formValues.address);
    formData.append('panNo', formValues.panNo);
    formData.append('dob', formValues.dob);
    formData.append('adminId', admin);
    const cleanedPins = Array.from(
      new Set(
        (selectedPincodes || [])
          .filter((p) => p)
          .map((p) => String(p).trim())
          .filter((p) => /^\d{6}$/.test(p))
      )
    );
    formData.append('pinCode', JSON.stringify(cleanedPins));

    try {
      const res = state?.id
        ? await updateAdmin({ formData, id: state.id })
        : await addAdmin(formData);

      // Handle RTK Query response structure
      if (res.error) {
        const errorData = res.error?.data;
        const errorMessage = errorData?.message || "Failed to save sub admin";

        if (errorMessage?.toLowerCase().includes("email already exists")) {
          setErrorMessages((prev) => ({ ...prev, email: errorMessage }));
        } else if (errorMessage?.toLowerCase().includes("mobile number already exists")) {
          setErrorMessages((prev) => ({ ...prev, mobile: errorMessage }));
        }

        // Check for blocking orders in error response
        if (errorData?.blockingOrders && errorData.blockingOrders.length > 0) {
          const firstBlocking = errorData.blockingOrders[0];
          setOrderDialog({
            open: true,
            pincode: firstBlocking.pincode,
            orders: firstBlocking.orders.map((orderNumber) => ({ orderNumber })),
            totalOrders: firstBlocking.orders.length,
          });
        } else {
          showMessage("error", errorMessage);
        }
      } else if (res.data) {
        const { status, message } = res.data;
        if (status) {
          navigate("/sub-admin");
          showMessage("success", message || "Sub admin saved successfully");
        } else {
          if (message?.toLowerCase().includes("email already exists")) {
            setErrorMessages((prev) => ({ ...prev, email: message }));
          } else if (message?.toLowerCase().includes("mobile number already exists")) {
            setErrorMessages((prev) => ({ ...prev, mobile: message }));
          }

          // Check for blocking orders in unsuccessful response
          if (res.data?.blockingOrders && res.data.blockingOrders.length > 0) {
            const firstBlocking = res.data.blockingOrders[0];
            setOrderDialog({
              open: true,
              pincode: firstBlocking.pincode,
              orders: firstBlocking.orders.map((orderNumber) => ({
                orderNumber,
              })),
              totalOrders: firstBlocking.orders.length,
            });
          } else {
            showMessage("error", message || "Failed to save sub admin");
          }
        }
      }
    } catch (error) {
      console.error("Failed to save the sub admin:", error);

      const errorMessage =
        error?.data?.message || "An unexpected error occurred";

      if (errorMessage?.toLowerCase().includes("email already exists")) {
        setErrorMessages((prev) => ({ ...prev, email: errorMessage }));
      } else if (errorMessage?.toLowerCase().includes("mobile number already exists")) {
        setErrorMessages((prev) => ({ ...prev, mobile: errorMessage }));
      }

      // Handle error with order details
      if (error?.data?.blockingOrders && error.data.blockingOrders.length > 0) {
        const firstBlocking = error.data.blockingOrders[0];
        setOrderDialog({
          open: true,
          pincode: firstBlocking.pincode,
          orders: firstBlocking.orders.map((orderNumber) => ({ orderNumber })),
          totalOrders: firstBlocking.orders.length,
        });
      } else {
        showMessage("error", errorMessage);
      }
    }
  };

  useEffect(() => {
    if (state) {
      setFormValues({
        id: state.id,
      });
    }
  }, [state]);

  useEffect(() => {
    if (subAdminDetails && state) {
      setFormValues({
        name: subAdminDetails?.name,
        mobile: subAdminDetails?.mobile,
        email: subAdminDetails?.email,
        guardianName: subAdminDetails?.guardianName,
        guardianMobile: subAdminDetails?.guardianMobile,
        address: subAdminDetails?.address,
        panNo: subAdminDetails?.panNo,
        dob: new Date(subAdminDetails?.dob).toISOString().split('T')[0],
        id: state.id,
      });
      setProfileImage(subAdminDetails?.profileImage);
      setAadharImage(subAdminDetails?.aadharImage);
      setBloodGroup(subAdminDetails?.bloodGroup);
      setStates(subAdminDetails?.state);
      setCity(subAdminDetails?.city);
      setAdmin(subAdminDetails?.adminId);
      const pincodes = subAdminDetails?.pinCodeData?.map(item => item.pincode) || [];
      setSelectedPincodes(pincodes || []);
    }
  }, [subAdminDetails, state]);

  return (
    <>
      <Loader open={isAdding || isUpdating} />
      <Stack flexDirection={'row'} gap={1} mb={1}>
        <BackButton />
        <Heading head='Add Sub Admin' />
      </Stack>
      <Box className='card_container'>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>User Photo</Typography>
            <UploadImage files={profileImage} setFiles={setProfileImage} />
            <ErrorMessage message={profileErrorMessage} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Name' name="name" value={formValues.name} onChange={handleInputChange} />
            {errorMessages.name && <ErrorMessage message={errorMessages.name} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Mobile' type='number' name="mobile" value={formValues.mobile} onChange={handleInputChange} />
            {errorMessages.mobile && <ErrorMessage message={errorMessages.mobile} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Email' type='email' name="email" value={formValues.email} onChange={handleInputChange} />
            {errorMessages.email && <ErrorMessage message={errorMessages.email} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Guardian Name' name="guardianName" value={formValues.guardianName} onChange={handleInputChange} />
            {errorMessages.guardianName && <ErrorMessage message={errorMessages.guardianName} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Guardian Mobile' name="guardianMobile" value={formValues.guardianMobile} onChange={handleInputChange} />
            {errorMessages.guardianMobile && <ErrorMessage message={errorMessages.guardianMobile} />}
          </Grid>
          {
            adminRole === 1 &&
            <Grid item xs={12} sm={6} md={4}>
              <Select selectList={adminListLoading ? [] : adminList} labelinput="Select Admin" name="admin" value={admin} onChange={handleAdminChange} />
              {errorMessages.admin && <ErrorMessage message={errorMessages.admin} />}
            </Grid>
          }
          <Grid item xs={12}>
            <Input labelinput='Address' name="address" value={formValues.address} onChange={handleInputChange} />
            {errorMessages.address && <ErrorMessage message={errorMessages.address} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select selectList={countryList} labelinput='Country' name="country" value={country} onChange={handleCountryChange} />
            {errorMessages.country && <ErrorMessage message={errorMessages.country} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select selectList={stateLoading ? [] : stateList} disabled={adminRole === 1 ? false : true} labelinput='State' name="state" value={states} onChange={handleStateChange} />
            {errorMessages.state && <ErrorMessage message={errorMessages.state} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select selectList={cityLoading ? [] : cityList} disabled={adminRole === 1 ? false : true} labelinput='City' name="city" value={city} onChange={handleCityChange} />
            {errorMessages.city && <ErrorMessage message={errorMessages.city} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Input labelinput='Pan Card No' name="panNo" value={formValues.panNo} onChange={handleInputChange} />
            {errorMessages.panNo && <ErrorMessage message={errorMessages.panNo} />}
          </Grid>

          {admin && (
            <Grid item xs={12}>
              <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>
                {isSelectedAdminSuper
                  ? 'Select Pincodes (All pincodes for selected City)'
                  : 'Select Pincodes (Assigned to Selected Admin)'}
              </Typography>
              <Autocomplete
                multiple
                id="pincode-admin-select"
                options={isSelectedAdminSuper ? cityPincodesForSuperAdmin : adminPincodes}
                loading={isSelectedAdminSuper ? cityExtraPincodeLoading : adminDetailLoading}
                disableCloseOnSelect
                getOptionLabel={(option) => option.pincode || ''}
                isOptionEqualToValue={(option, value) =>
                  (option?.pincode || '') === (value?.pincode || '')
                }
                value={(isSelectedAdminSuper ? cityPincodesForSuperAdmin : adminPincodes).filter((p) =>
                  selectedPincodes.includes(p.pincode)
                )}
                onChange={(event, newValue) => {
                  const pins = newValue.map((item) => item.pincode);
                  setSelectedPincodes(pins);
                  setErrorMessages(prev => ({ ...prev, pincode: '' }));
                }}
                getOptionDisabled={(option) => {
                  const ownerSubAdminId = pincodeOwnershipMap[option.pincode];
                  if (!ownerSubAdminId) return false;
                  if (state?.id && Number(ownerSubAdminId) === Number(state.id)) return false;
                  return true;
                }}
                renderOption={(props, option, { selected }) => {
                  const ownerSubAdminId = pincodeOwnershipMap[option.pincode];
                  const isAssignedToOther =
                    !!ownerSubAdminId && (!state?.id || Number(ownerSubAdminId) !== Number(state.id));

                  const { key, ...optionProps } = props;
                  return (
                    <li
                      key={key}
                      {...optionProps}
                      style={{
                        backgroundColor: isAssignedToOther ? '#e3f2fd' : '#ffffff',
                        opacity: isAssignedToOther ? 0.7 : 1,
                      }}
                    >
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                        disabled={isAssignedToOther}
                      />
                      <span>{option.pincode}</span>
                      {isAssignedToOther && (
                        <Typography component="span" fontSize="11px" color="text.secondary" ml={1}>
                          (Already assigned)
                        </Typography>
                      )}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      (isSelectedAdminSuper ? cityExtraPincodeLoading : adminDetailLoading)
                        ? "Loading pincodes..."
                        : "Search & select pincodes"
                    }
                  />
                )}
              />
              {errorMessages.pincode && <ErrorMessage message={errorMessages.pincode} />}
              {!isSelectedAdminSuper && !adminDetailLoading && adminPincodes.length === 0 && admin && (
                <Typography fontSize={{ xs: '11px', sm: '12px' }} color="text.secondary" mt={0.5}>
                  No pincodes assigned to this admin.
                </Typography>
              )}
              {isSelectedAdminSuper && !cityExtraPincodeLoading && city && cityPincodesForSuperAdmin.length === 0 && (
                <Typography fontSize={{ xs: '11px', sm: '12px' }} color="text.secondary" mt={0.5}>
                  No pincodes found for this city.
                </Typography>
              )}
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>Upload Aadhar Card</Typography>
            <UploadImage files={aadharImage} setFiles={setAadharImage} />
            <ErrorMessage message={addharErrorMessage} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Select selectList={bloodGroupList} labelinput='Blood Group' name="bloodGroup" value={bloodGroup} onChange={handleBloodGroupChange} />
            {errorMessages.bloodGroup && <ErrorMessage message={errorMessages.bloodGroup} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='User DOB' name="dob" type='date' value={formValues.dob} onChange={handleInputChange} />
            {errorMessages.dob && <ErrorMessage message={errorMessages.dob} />}
          </Grid>
          <Grid item xs={12}>
            <Stack flexDirection={'row'} gap={1}>
              <Button buttonName='save' onClick={handleFormSubmit} />
              <Button buttonName='cancel' color='white' onClick={() => navigate('/sub-admin')} />
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* ORDER ASSOCIATION WARNING DIALOG */}
      <Dialog
        open={orderDialog.open}
        onClose={handleCloseOrderDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Cannot Remove Pincode
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Pincode <strong>{orderDialog.pincode}</strong> is associated with{' '}
            <strong>{orderDialog.totalOrders}</strong> active order(s) and cannot be removed from this sub-admin.
          </DialogContentText>

          {orderDialog.orders && orderDialog.orders.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Associated Orders:
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order Number</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderDialog.orders.map((order, index) => (
                      <TableRow key={index}>
                        <TableCell>{order.orderNumber}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <DialogContentText sx={{ mt: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
            You can only remove pincodes from sub-admins if the associated orders have status:
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>Upcoming/Placed</li>
              <li>Assigned to Technician</li>
              <li>Completed</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button buttonName="Close" onClick={handleCloseOrderDialog} />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddPincodeAdmin;